/**
 * Boolean operations on closed construction shapes (#764).
 *
 * Union / Subtract / Intersect / Exclude / Divide, applied to two or more
 * closed construction shapes. The operands are consumed and replaced by the
 * result, in the first operand's slot in the Parts list — the same shape the
 * Join action has (see `joinConstructionSelection`), for the same reason:
 * a boolean op is a rewrite of the geometry, not an overlay on top of it.
 *
 * ## Why the operand order is the Parts-list order
 *
 * Construction lines have no fill and no z-occlusion, so Illustrator's
 * front / back framing has nothing to hang on here. Selection click order
 * isn't dependable either — a marquee or a Select-All produces a set, not a
 * sequence. So the rule is the one thing that IS visible and stable: the
 * order the shapes appear in the Parts list. For Subtract, the topmost
 * selected shape is the one that keeps its material and every other selected
 * shape is taken out of it. The other four ops are order-independent
 * (Divide and Exclude are symmetric; Unite and Intersect are associative and
 * commutative), so the order only decides where the result lands.
 *
 * ## Divide is the full arrangement, not paper's `divide`
 *
 * paper's `PathItem#divide` returns only `A−B` and `A∩B` — it drops `B−A`.
 * That is not what "split into overlapping elements" means, so Divide here
 * builds every non-empty region of the arrangement itself: for each non-empty
 * subset S of the operands, the region inside every member of S and outside
 * every non-member. Two overlapping shapes give the three regions a maker
 * would expect. It costs 2^n region builds, hence `DIVIDE_MAX_OPERANDS`.
 *
 * ## What is not an operand
 *
 * Open lines (a polyline has no inside), degenerate shapes with no area, and
 * DERIVED lines — offset children and mirror children. A derived line's
 * geometry is recomputed from its source on every commit, so consuming one
 * would either be undone by the next derive pass or silently orphan the
 * relationship. Split excludes them for the same reason
 * (`canSplitConstructionLine`).
 */

export type ShapeBooleanOp =
  | "unite"
  | "subtract"
  | "intersect"
  | "exclude"
  | "divide";

/** The ops as the panel offers them, in the order they are shown.
 *  `resultModifier` is the auto-name stem the results take, so a glance at
 *  the Parts list says which op produced what. */
export const SHAPE_BOOLEAN_OPS: ReadonlyArray<{
  op: ShapeBooleanOp;
  label: string;
  resultModifier: string;
  title: string;
}> = [
  {
    op: "unite",
    label: "Unite",
    resultModifier: "Union",
    title:
      "Merge the selected shapes into one outline. Overlaps become a single region; shapes that don't touch stay as separate subpaths of one shape.",
  },
  {
    op: "subtract",
    label: "Subtract",
    resultModifier: "Difference",
    title:
      "Cut the other selected shapes out of the topmost one in the Parts list.",
  },
  {
    op: "intersect",
    label: "Intersect",
    resultModifier: "Intersection",
    title: "Keep only the area covered by every selected shape.",
  },
  {
    op: "exclude",
    label: "Exclude",
    resultModifier: "Exclusion",
    title:
      "Keep the area covered by an odd number of the selected shapes — the overlaps become holes.",
  },
  {
    op: "divide",
    label: "Divide",
    resultModifier: "Region",
    title:
      "Split the selected shapes into one separate shape per overlapping region.",
  },
];

/** Two shapes are the minimum for every op — one shape has nothing to
 *  combine with. */
export const MIN_OPERANDS = 2;

/** Divide builds one region per non-empty subset of the operands, so its cost
 *  is exponential. Six shapes is 63 regions, which is already far past what a
 *  pattern needs; beyond that the button stays disabled rather than hanging
 *  the tab. The other ops are linear and use `MAX_OPERANDS`. */
export const DIVIDE_MAX_OPERANDS = 6;

/** A generous ceiling for the linear ops — high enough never to be reached in
 *  practice, low enough that a runaway selection can't lock the UI. */
export const MAX_OPERANDS = 64;

/** Areas below this (mm²) are treated as nothing: a 0.001 mm square. Boolean
 *  ops routinely leave slivers of numerical noise along a shared edge, and a
 *  sliver is not a shape a maker asked for. */
const AREA_EPSILON_MM2 = 1e-6;

/** Parse path data into a closed paper shape, or null when it isn't one. Every
 *  subpath must be closed and the whole thing must enclose real area — an open
 *  polyline has no inside for a boolean op to reason about. Caller owns
 *  `remove()` on a non-null result. */
export function closedShapeFor(
  pathData: string,
  scope: paper.PaperScope,
): paper.PathItem | null {
  if (!pathData) return null;
  let item: paper.CompoundPath;
  try {
    item = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths = (item.children ?? []) as paper.Path[];
  if (subpaths.length === 0) {
    item.remove();
    return null;
  }
  for (const sub of subpaths) {
    // A 2-segment "closed" path is a doubled-back line, not a region.
    if (!sub.closed || sub.segments.length < 3) {
      item.remove();
      return null;
    }
  }
  if (Math.abs(item.area) < AREA_EPSILON_MM2) {
    item.remove();
    return null;
  }
  return item;
}

/** True when this path data is a closed region a boolean op can use. Cheap
 *  enough to run per selected object on every toolbar sync. */
export function isClosedShapePath(
  pathData: string,
  scope: paper.PaperScope,
): boolean {
  const shape = closedShapeFor(pathData, scope);
  if (!shape) return false;
  shape.remove();
  return true;
}

/** Whether `op` could do something with this many operands. Structural only:
 *  it does NOT run the op, so an Intersect of two shapes that turn out not to
 *  overlap still reads as available. Running every op on every render to find
 *  out would cost far more than it saves, and the action declines cleanly when
 *  the result comes back empty. */
export function canShapeBooleanWith(
  operandCount: number,
  op: ShapeBooleanOp,
): boolean {
  if (operandCount < MIN_OPERANDS) return false;
  const max = op === "divide" ? DIVIDE_MAX_OPERANDS : MAX_OPERANDS;
  return operandCount <= max;
}

/** `area` is declared on Path and CompoundPath but not on their shared
 *  `PathItem` base in paper's typings, and a boolean op is typed as returning
 *  the base. Both concrete types carry it, so read it through a narrow cast
 *  rather than branching on the runtime class. */
function shapeArea(item: { area?: number }): number {
  return typeof item.area === "number" ? item.area : 0;
}

/**
 * GROSS enclosed area — every subpath's magnitude, added up.
 *
 * Deliberately not `Math.abs(item.area)`, which is the magnitude of the SIGNED
 * SUM. A boolean op can hand back two disjoint regions wound in opposite
 * directions; their signed areas then cancel and the sum reads as zero for a
 * shape that plainly encloses something. Gross area is what an emptiness test
 * wants — "is there any region here at all" — and it can't be fooled that way.
 * It over-counts holes, which is why it is used only for the emptiness test and
 * never as a measurement.
 */
function grossArea(item: paper.PathItem): number {
  const children = (item as unknown as { children?: paper.Path[] }).children;
  if (children && children.length > 0) {
    let sum = 0;
    for (const child of children) sum += Math.abs(shapeArea(child));
    return sum;
  }
  return Math.abs(shapeArea(item as unknown as { area?: number }));
}

function isEmptyShape(item: paper.PathItem | null): boolean {
  if (!item) return true;
  return item.isEmpty() || grossArea(item) < AREA_EPSILON_MM2;
}

/** Split a boolean result into the shapes it should become. Everything except
 *  Divide stays ONE construction line even when the result is disjoint — a
 *  union of two separate shapes is one object with two subpaths, and holes
 *  have to stay attached to the ring that contains them. Divide is the whole
 *  point of the opposite, so its regions come back separately. */
function resultPathData(
  op: ShapeBooleanOp,
  results: paper.PathItem[],
): string[] {
  return results
    .filter((r) => !isEmptyShape(r))
    .map((r) => {
      // Normalize the winding before the geometry is stored. paper's solver
      // can return disjoint regions wound in opposite directions — correct as
      // an outline, but it makes the SIGNED area of the whole shape cancel to
      // zero, and signed area is what the Dimensions readout measures with.
      // `reorient(false, true)` sets every root subpath clockwise and leaves a
      // genuine hole wound against its container, so both an Exclude result
      // and a donut measure as the leather they actually take.
      r.reorient(false, true);
      return r.pathData;
    })
    .filter((d) => d.trim().length > 0)
    .slice(0, op === "divide" ? Infinity : 1);
}

/** Every non-empty region of the arrangement: for each non-empty subset of the
 *  operands, the area inside all of its members and outside every other
 *  operand. Two overlapping shapes give three regions (A-only, both, B-only),
 *  which is what "split into overlapping elements" asks for and what paper's
 *  own `divide` does not produce. */
function divideRegions(shapes: paper.PathItem[]): paper.PathItem[] {
  const n = shapes.length;
  const out: paper.PathItem[] = [];
  for (let mask = 1; mask < 1 << n; mask++) {
    let region: paper.PathItem | null = null;
    for (let i = 0; i < n; i++) {
      if (!(mask & (1 << i))) continue;
      region =
        region === null
          ? (shapes[i]!.clone({ insert: false }) as paper.PathItem)
          : region.intersect(shapes[i]!, { insert: false });
      if (isEmptyShape(region)) break;
    }
    if (isEmptyShape(region)) continue;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) continue;
      region = region!.subtract(shapes[i]!, { insert: false });
      if (isEmptyShape(region)) break;
    }
    if (!isEmptyShape(region)) out.push(region!);
  }
  return out;
}

/**
 * Run `op` over `operands` (already in Parts-list order) and return the
 * pathData for the shapes it produces, or null when it produces nothing.
 *
 * Null is the honest answer for "these two shapes don't overlap, so their
 * intersection is empty". Illustrator deletes both operands in that case;
 * here the caller declines instead, because losing two shapes to a mis-click
 * is a worse outcome than a button that appears to do nothing.
 */
export function computeShapeBoolean(
  op: ShapeBooleanOp,
  operands: readonly string[],
  scope: paper.PaperScope,
): string[] | null {
  if (!canShapeBooleanWith(operands.length, op)) return null;
  const shapes: paper.PathItem[] = [];
  for (const pathData of operands) {
    const shape = closedShapeFor(pathData, scope);
    if (!shape) {
      for (const s of shapes) s.remove();
      return null;
    }
    shapes.push(shape);
  }
  try {
    let results: paper.PathItem[];
    if (op === "divide") {
      results = divideRegions(shapes);
    } else {
      let acc: paper.PathItem = shapes[0]!.clone({
        insert: false,
      }) as paper.PathItem;
      for (let i = 1; i < shapes.length; i++) {
        const other = shapes[i]!;
        acc =
          op === "unite"
            ? acc.unite(other, { insert: false })
            : op === "subtract"
              ? acc.subtract(other, { insert: false })
              : op === "intersect"
                ? acc.intersect(other, { insert: false })
                : acc.exclude(other, { insert: false });
        // Intersect and Subtract can empty out partway; folding on from
        // nothing just wastes work and can throw inside paper.
        if (isEmptyShape(acc)) break;
      }
      results = [acc];
    }
    const data = resultPathData(op, results);
    return data.length > 0 ? data : null;
  } catch {
    // paper's boolean solver can throw on pathological self-intersecting
    // input. Declining beats committing a half-built project.
    return null;
  } finally {
    for (const s of shapes) s.remove();
  }
}
